import { buildGmailQuery, parseFullMessage } from '~common/google-api/utils'
import * as api from '~common/google-api/gmail'
import type {
    GmailFullMessageResponse,
    GmailMessageParsed,
    ImporterQueryDateRange,
} from '~common/google-api/types'

export interface SearchProcessorDeps<TProcessedEmail> {}

export interface SearchAndScheduleParams {
    terms: string[]
    dateRange?: { from?: Date; to?: Date }
    dontProcessInvoices?: boolean
    pageSize?: number
    searchSubjectOnly?: boolean
    nextPageToken?: string
    accessToken: string
}

export interface SearchAndScheduleResult<TProcessedEmail> {
    processedEmails: TProcessedEmail[]
    preProcessedEmails: GmailMessageParsed[]
    nextPageToken: string | null
}

export async function performSearch(params: {
    terms: string
    dateRange: { from?: Date; to?: Date } | undefined
    nextPageToken?: string
    pageSize?: number
    searchSubjectOnly?: boolean
    accessToken: string
}): Promise<{
    parsedMessages: GmailMessageParsed[]
    nextPageToken: string | undefined
}> {
    let importerRange: ImporterQueryDateRange | undefined
    if (params.dateRange?.from || params.dateRange?.to) {
        const startMs = params.dateRange.from?.getTime() ?? 0
        const endMs = params.dateRange.to?.getTime() ?? Date.now()
        importerRange = {
            startMs,
            endMs,
        }
    }
    const termQuery: string = params.terms

    const q = buildGmailQuery(
        termQuery,
        importerRange,
        params.searchSubjectOnly,
    )
    const list = await api.listGmailMessages(params.accessToken, {
        q,
        pageToken: params.nextPageToken,
        maxResults: params.pageSize,
    })

    const messagesToFetch = list.messages.map((m) => m.id)

    const messages = await api.batchGetGmailMessages(params.accessToken, {
        messageIds: messagesToFetch,
    })

    const parsedMessages: GmailMessageParsed[] = (messages ?? [])
        .filter((m): m is GmailFullMessageResponse => m != null)
        .map((message) => parseFullMessage(message))

    return {
        parsedMessages,
        nextPageToken: list.nextPageToken ?? '',
    }
}
